/** Tabbed editor for one access group (console analog of the dashboard's).
 *
 * Tabs: General · Members · Permissions. Managed groups (template-controlled)
 * are read-only except membership; a banner links their state to the template
 * and offers Unmanage. Re-managing overwrites the group from the template.
 */
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';

import { accessGroupsApi, type AccessGroupDetail } from '@/api/accessGroups';
import { rbacApi, type EmployeePermission } from '@/api/rbac';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { HistoryPanel } from '@/components/History/HistoryPanel';
import { useSnackbar } from '@/components/Snackbar';
import { ValueActions, type ValueAction } from '@/components/ValueActions/ValueActions';
import {
  accessGroupIdActions,
  accessGroupSelfActions,
  rbacOperatorActions,
  userRefActions,
} from '@/features/users/userLinks';
import { usePermissions } from '@/hooks/usePermissions';

import {
  PermissionScopeSections,
  permissionAction,
  permissionScope,
} from './PermissionScopeSections';

interface Props {
  topId: number;
  groupId: number | null; // null = create
  onClose: () => void;
}

export function AccessGroupEditDialog({ topId, groupId, onClose }: Props) {
  const { hasPerm } = usePermissions();
  const canManage = hasPerm('console_rbac_manage');
  const snackbar = useSnackbar();
  const qc = useQueryClient();

  const [tab, setTab] = useState(0);
  const [name, setName] = useState('');
  const [codename, setCodename] = useState('');
  const [parentId, setParentId] = useState<number | ''>('');
  const [atagIds, setAtagIds] = useState<number[]>([]);
  const [roleIds, setRoleIds] = useState<number[]>([]);
  const [propagatedRoleIds, setPropagatedRoleIds] = useState<number[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmManage, setConfirmManage] = useState(false);

  const detailQuery = useQuery({
    queryKey: ['accessGroup', topId, groupId],
    queryFn: () => accessGroupsApi.retrieve(topId, groupId!),
    enabled: groupId !== null,
  });
  const detail = detailQuery.data;
  const managed = Boolean(detail?.managed && detail?.template);

  const groupsQuery = useQuery({
    queryKey: ['accessGroups', topId],
    queryFn: () => accessGroupsApi.list(topId),
  });
  const tagsQuery = useQuery({
    queryKey: ['accessTags', topId],
    queryFn: () => rbacApi.listAccessTags(topId),
  });
  const rolesQuery = useQuery({
    queryKey: ['employeeRoles', topId],
    queryFn: () => rbacApi.listEmployeeRoles(topId),
  });
  const permsQuery = useQuery({
    queryKey: ['employeePermissions'],
    queryFn: () => rbacApi.listEmployeePermissions(),
  });

  useEffect(() => {
    if (groupId === null || !detail || loaded) return;
    setName(detail.name);
    setCodename(detail.codename ?? '');
    setParentId(detail.parent ?? '');
    setAtagIds(detail.atag.map((t) => t.id));
    setRoleIds(detail.roles.map((r) => r.id));
    setPropagatedRoleIds(detail.propagated_roles.map((r) => r.id));
    setLoaded(true);
  }, [detail, groupId, loaded]);

  function refetchDetail() {
    qc.invalidateQueries({ queryKey: ['accessGroup', topId, groupId] });
    qc.invalidateQueries({ queryKey: ['accessGroups'] });
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        name: name.trim(),
        codename: codename.trim() || null,
        parent: parentId === '' ? null : parentId,
        atag_ids: atagIds,
        role_ids: roleIds,
        propagated_role_ids: propagatedRoleIds,
      };
      return groupId === null
        ? accessGroupsApi.create(topId, body)
        : accessGroupsApi.update(topId, groupId, body);
    },
    onSuccess: () => {
      refetchDetail();
      snackbar.show('Access group saved', 'success');
      onClose();
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  const manageMutation = useMutation({
    mutationFn: (value: boolean) => accessGroupsApi.manage(topId, groupId!, value),
    onSuccess: (d) => {
      refetchDetail();
      const warnings = d.warnings ?? [];
      snackbar.show(
        d.managed ? 'Group re-managed: rebuilt from its template' : 'Group unmanaged: now editable',
        warnings.length ? 'warning' : 'success',
      );
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  const removeUserMutation = useMutation({
    mutationFn: (userId: number) => accessGroupsApi.removeUser(topId, groupId!, [userId]),
    onSuccess: () => refetchDetail(),
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });

  const readOnly = !canManage || managed;
  const parentOptions = (groupsQuery.data?.results ?? []).filter((g) => g.id !== groupId);

  return (
    <Dialog open onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {groupId === null || !detail ? (
          groupId === null ? 'New access group' : `Access group “${detail?.name ?? ''}”`
        ) : (
          <>
            {'Access group '}
            <ValueActions actions={accessGroupSelfActions(detail, topId)}>
              {`“${detail.name}”`}
            </ValueActions>
            <Typography variant="body2" color="text.secondary">
              <ValueActions actions={rbacOperatorActions({ id: topId })}>
                Tour operator #{topId}
              </ValueActions>
            </Typography>
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {managed && detail?.template && (
          <Alert
            severity="info"
            sx={{ mb: 2 }}
            action={
              canManage ? (
                <Button color="inherit" size="small" onClick={() => manageMutation.mutate(false)}>
                  Unmanage
                </Button>
              ) : undefined
            }
          >
            Managed by template <strong>{detail.template.name}</strong> — settings and permissions are
            read-only here; edit the template instead. Membership stays editable.
            {detail.bindings.length > 0 && (
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                Bindings: {detail.bindings.map((b) => `${b.argument_alias} = ${b.atag.name}`).join(', ')}
              </Typography>
            )}
          </Alert>
        )}
        {!managed && detail?.template && canManage && (
          <Alert
            severity="warning"
            sx={{ mb: 2 }}
            action={
              <Button color="inherit" size="small" onClick={() => setConfirmManage(true)}>
                Manage
              </Button>
            }
          >
            Unmanaged copy of template <strong>{detail.template.name}</strong>. Re-managing overwrites
            this group&apos;s settings and permissions with the template state.
          </Alert>
        )}

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
          <Tab label="General" />
          {groupId !== null && <Tab label={`Members (${detail?.users.length ?? 0})`} />}
          {groupId !== null && <Tab label={`Permissions (${detail?.permissions.length ?? 0})`} />}
          {groupId !== null && hasPerm('console_audit_view') && <Tab label="History" />}
        </Tabs>

        {tab === 0 && (
          <Stack spacing={2}>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                fullWidth
                disabled={readOnly}
                inputProps={{ autoComplete: 'off' }}
              />
              <TextField
                label="Code name"
                value={codename}
                onChange={(e) => setCodename(e.target.value)}
                fullWidth
                disabled={readOnly}
                inputProps={{ autoComplete: 'off' }}
              />
            </Stack>
            <Box>
              <TextField
                select
                fullWidth
                label="Parent group"
                value={parentId}
                onChange={(e) => setParentId(e.target.value === '' ? '' : Number(e.target.value))}
                disabled={readOnly}
              >
                <MenuItem value="">None</MenuItem>
                {parentOptions.map((g) => (
                  <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>
                ))}
              </TextField>
              {/* The select owns its click (it is how the parent is CHANGED);
                  the picked value's own links live beside it. */}
              {parentId !== '' && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
                  <ValueActions actions={accessGroupIdActions(parentId)}>
                    {parentOptions.find((g) => g.id === parentId)?.name ?? `#${parentId}`}
                  </ValueActions>
                </Typography>
              )}
            </Box>
            <MultiSelect
              label="Access tags"
              options={(tagsQuery.data?.results ?? []).map((t) => ({ id: t.id, name: t.name }))}
              value={atagIds}
              onChange={setAtagIds}
              disabled={readOnly}
            />
            <Stack direction="row" spacing={2}>
              <MultiSelect
                label="Roles"
                options={rolesQuery.data?.results ?? []}
                value={roleIds}
                onChange={setRoleIds}
                disabled={readOnly}
              />
              <MultiSelect
                label="Propagated roles"
                options={rolesQuery.data?.results ?? []}
                value={propagatedRoleIds}
                onChange={setPropagatedRoleIds}
                disabled={readOnly}
              />
            </Stack>
          </Stack>
        )}

        {tab === 1 && groupId !== null && (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              Membership is editable even for managed groups. Users are added via the tenant dashboard.
            </Typography>
            {(detail?.users ?? []).map((u) => (
              <Stack key={u.id} direction="row" spacing={2} alignItems="center">
                <Typography sx={{ flex: 1 }}>
                  <ValueActions actions={userRefActions(u, { tenantId: topId })}>
                    {u.display_name || u.email}
                  </ValueActions>
                </Typography>
                <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                {canManage && (
                  <IconButton size="small" onClick={() => removeUserMutation.mutate(u.id)}>
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                )}
              </Stack>
            ))}
            {(detail?.users ?? []).length === 0 && (
              <Typography variant="body2" color="text.secondary">No direct members.</Typography>
            )}
          </Stack>
        )}

        {tab === 2 && groupId !== null && detail && (
          <GroupPermissionsEditor
            topId={topId}
            group={detail}
            permCatalog={permsQuery.data?.results ?? []}
            readOnly={readOnly}
            onChanged={refetchDetail}
          />
        )}
        {tab === 3 && groupId !== null && (
          <HistoryPanel
            contentType="demo.access_group"
            objectPk={String(groupId)}
            aggregate
            onRestored={() => {
              void detailQuery.refetch();
              void qc.invalidateQueries({ queryKey: ['accessGroups', topId] });
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        {!readOnly && tab === 0 && (
          <Button
            variant="contained"
            disabled={!name.trim() || saveMutation.isPending || (groupId !== null && !loaded)}
            onClick={() => saveMutation.mutate()}
          >
            Save
          </Button>
        )}
      </DialogActions>

      <ConfirmDialog
        open={confirmManage}
        title="Re-manage this group?"
        message="The next step overwrites this group's name, roles and ALL permissions with the template state. Membership is kept."
        confirmLabel="Manage"
        onConfirm={() => {
          setConfirmManage(false);
          manageMutation.mutate(true);
        }}
        onCancel={() => setConfirmManage(false)}
      />
    </Dialog>
  );
}

function MultiSelect({
  label,
  options,
  value,
  onChange,
  disabled,
  compact = false,
  chipActions,
}: {
  label: string;
  options: { id: number; name: string }[];
  value: number[];
  onChange: (v: number[]) => void;
  disabled?: boolean;
  /** Dense inline variant for permission rows: small, underline instead of the
   *  outlined box, and only a few chips shown when unfocused. */
  compact?: boolean;
  /** Quick actions for a picked value. The chip keeps its own delete click —
   *  ValueActions only adds a hover/keyboard popover around it. */
  chipActions?: (id: number) => ValueAction[];
}) {
  const byId = useMemo(() => new Map(options.map((o) => [o.id, o])), [options]);
  return (
    <Autocomplete<number, true>
      multiple
      fullWidth
      size={compact ? 'small' : 'medium'}
      limitTags={compact ? 3 : -1}
      options={options.map((o) => o.id)}
      getOptionLabel={(id) => byId.get(id)?.name ?? `#${id}`}
      value={value}
      onChange={(_, v) => onChange(v)}
      disabled={disabled}
      renderTags={(val, getTagProps) =>
        val.map((id, index) => {
          const chip = (
            <Chip label={byId.get(id)?.name ?? `#${id}`} size="small" {...getTagProps({ index })} key={id} />
          );
          return chipActions ? (
            <ValueActions key={id} chip actions={chipActions(id)}>
              {chip}
            </ValueActions>
          ) : (
            chip
          );
        })
      }
      renderInput={(params) => (
        <TextField {...params} label={label} variant={compact ? 'standard' : 'outlined'} />
      )}
    />
  );
}

/** Scope-grouped permission editor operating directly against the API.
 *
 * Grant/revoke (single and bulk) go through the batch_permissions endpoint —
 * one request per click; tag/target/all-tags edits use the aps endpoint.
 */
function GroupPermissionsEditor({
  topId,
  group,
  permCatalog,
  readOnly,
  onChanged,
}: {
  topId: number;
  group: AccessGroupDetail;
  permCatalog: EmployeePermission[];
  readOnly: boolean;
  onChanged: () => void;
}) {
  const snackbar = useSnackbar();
  const tagsQuery = useQuery({
    queryKey: ['accessTags', topId],
    queryFn: () => rbacApi.listAccessTags(topId),
  });
  const groupsQuery = useQuery({
    queryKey: ['accessGroups', topId],
    queryFn: () => accessGroupsApi.list(topId),
  });

  const granted = useMemo(
    () => new Set(group.permissions.map((p) => p.perm.codename)),
    [group.permissions],
  );
  const apsByCodename = useMemo(
    () => new Map(group.permissions.map((p) => [p.perm.codename, p])),
    [group.permissions],
  );

  type BatchAction = {
    op: 'add' | 'remove' | 'set_all_tags';
    scope: string;
    action: string;
    value?: boolean;
  };
  const toAction = (perm: EmployeePermission, op: BatchAction['op'], value?: boolean): BatchAction => ({
    op,
    scope: permissionScope(perm.codename),
    action: permissionAction(perm.codename),
    ...(value === undefined ? {} : { value }),
  });
  const batchMutation = useMutation({
    mutationFn: (actions: BatchAction[]) =>
      accessGroupsApi.batchPermissions(topId, group.id, actions),
    onSuccess: (detail) => {
      onChanged();
      const skipped = detail.skipped ?? [];
      if (skipped.length) {
        snackbar.show(`${skipped.length} action(s) were not applied`, 'warning');
      }
    },
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });
  const apsMutation = useMutation({
    mutationFn: (body: { aps_id: number; atags?: number[]; target_agroups?: number[]; all_tags?: boolean }) =>
      accessGroupsApi.aps(topId, group.id, body),
    onSuccess: () => onChanged(),
    onError: (e) => snackbar.show((e as Error).message, 'error'),
  });
  const busy = batchMutation.isPending || apsMutation.isPending;

  function renderControls(perm: EmployeePermission) {
    const aps = apsByCodename.get(perm.codename);
    if (!aps) return null;
    return (
      <>
        {perm.has_target_tags && (
          <>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Switch
                size="small"
                checked={aps.all_tags}
                disabled={readOnly || busy}
                onChange={(e) => apsMutation.mutate({ aps_id: aps.id, all_tags: e.target.checked })}
              />
              <Typography variant="caption">all tags</Typography>
            </Box>
            <MultiSelect
              compact
              label="Access tags"
              options={(tagsQuery.data?.results ?? []).map((t) => ({ id: t.id, name: t.name }))}
              value={aps.atags.map((t) => t.id)}
              onChange={(ids) => apsMutation.mutate({ aps_id: aps.id, atags: ids })}
              disabled={readOnly || aps.all_tags || busy}
            />
          </>
        )}
        {perm.has_target_agroups && (
          <MultiSelect
            compact
            label="Target groups"
            options={(groupsQuery.data?.results ?? []).map((g) => ({
              id: g.id,
              name: g.is_guest_group ? `${g.name} [*]` : g.name,
            }))}
            value={aps.target_agroups.map((t) => t.id)}
            onChange={(ids) => apsMutation.mutate({ aps_id: aps.id, target_agroups: ids })}
            disabled={readOnly || busy}
            chipActions={accessGroupIdActions}
          />
        )}
      </>
    );
  }

  return (
    <PermissionScopeSections
      catalog={permCatalog}
      granted={granted}
      readOnly={readOnly}
      busy={busy}
      onToggle={(perm, grant) => batchMutation.mutate([toAction(perm, grant ? 'add' : 'remove')])}
      onBulk={(perms, grant) =>
        batchMutation.mutate(perms.map((perm) => toAction(perm, grant ? 'add' : 'remove')))
      }
      allTagsOf={(perm) => Boolean(apsByCodename.get(perm.codename)?.all_tags)}
      onBulkAllTags={(perms, value) =>
        batchMutation.mutate(perms.map((perm) => toAction(perm, 'set_all_tags', value)))
      }
      renderControls={renderControls}
    />
  );
}
